import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static get targets() {
    return ["button", "input", "search", "option", "addFolder", "newFolderName", "noFolder", "clearButton"]
  }

  static get values() {
    return {
      folderManagementUrl: String,
      type: String,
      folderId: Number,
    }
  }

  connect() {
    // Listen for dropdown show event
    this.element.addEventListener("show.bs.dropdown", this.focusSearch.bind(this))

    // Show clear button if a folder is already selected
    if (this.folderIdValue) {
      this.clearButtonTarget.classList.remove("d-none")
    }
  }

  disconnect() {
    this.element.removeEventListener("show.bs.dropdown", this.focusSearch.bind(this))
  }

  focusSearch() {
    // Focus on search field when dropdown opens
    requestAnimationFrame(() => this.searchTarget.focus())
  }

  search() {
    const searchValue = this.searchTarget.value.toLowerCase()
    let anySameName = false

    this.optionTargets.forEach((option) => {
      let optionName = option.dataset.name.toLowerCase()
      if (optionName === searchValue) {
        anySameName = true
      }

      if (optionName.includes(searchValue)) {
        option.classList.remove("d-none")
      } else {
        option.classList.add("d-none")
      }
    })

    if (searchValue !== "" && !anySameName) {
      this.addFolderTarget.classList.remove("d-none")
      this.newFolderNameTarget.textContent = this.searchTarget.value
    } else {
      this.addFolderTarget.classList.add("d-none")
      this.newFolderNameTarget.textContent = ""
    }
  }

  addFolder() {
    if (this.addFolderTarget.classList.contains("d-none")) {
      return
    }

    fetch(this.folderManagementUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
      },
      body: JSON.stringify({ folder_name: this.searchTarget.value }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.turbo_streams) {
          data.turbo_streams.forEach((turboStream) => {
            Turbo.renderStreamMessage(turboStream)
          })
        }

        // Wait for Turbo Stream to finish and DOM to update
        requestAnimationFrame(() => {
          if (data.success) {
            this.addFolderTarget.classList.add("d-none")
            this.newFolderNameTarget.textContent = ""
            this.#setFolder(data.folder_id)
            this.element.click()
          }
        })
      })
      .catch(() => {
        App.displayFlash("danger", "Failed to create folder.")
      })
  }

  clearFolder() {
    this.#clearFolder()

    // Close the dropdown
    this.element.click()

    // Remove focus from the button, otherwise it will be highlighted
    setTimeout(() => {
      this.buttonTarget.blur()
    }, 10)
  }

  setFolder({ params: { folderId } }) {
    this.#setFolder(folderId)

    // Close the dropdown
    this.element.click()
  }

  #setFolder(folderId) {
    this.searchTarget.value = ""
    this.search()

    const selectedOption = this.optionTargets.find((option) => parseInt(option.dataset.id) === parseInt(folderId))
    this.buttonTarget.innerHTML = selectedOption.querySelector("button").innerHTML
    this.inputTarget.value = folderId
    this.buttonTarget.dataset.id = folderId
    this.folderIdValue = folderId
    this.clearButtonTarget.classList.remove("d-none")
  }

  #clearFolder() {
    this.buttonTarget.innerHTML = this.noFolderTarget.innerHTML
    this.inputTarget.value = ""
    this.buttonTarget.dataset.id = ""
    this.folderIdValue = null
    this.clearButtonTarget.classList.add("d-none")
  }
}
